import { Download, RefreshCw, X, Share } from "lucide-react"
import { useState, type ReactNode } from "react"
import { usePwaInstall, usePwaUpdate, useIosInstall } from "@/hooks/usePwa"
import { Button } from "@/components/ui/button"

/** Aviso flutuante. Usa <aside> (não div) para não colidir com os seletores div.fixed dos modais nos testes. */
function Floating({ label, children }: { label: string; children: ReactNode }) {
  return (
    <aside aria-label={label}
      className="fixed bottom-20 lg:bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-sm rounded-lg border bg-card shadow-overlay px-4 py-3 text-[14px]">
      {children}
    </aside>
  )
}

export function PwaBanner() {
  const { canInstall, install } = usePwaInstall()
  const { needRefresh, updateServiceWorker } = usePwaUpdate()
  const { show: showIos, dismiss: dismissIos } = useIosInstall()
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  if (needRefresh) {
    return (
      <Floating label="Atualização disponível">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="flex-1">Há uma versão nova do FinControl.</span>
          <Button size="sm" onClick={() => updateServiceWorker(true)}>Atualizar</Button>
          <button type="button" aria-label="Dispensar" onClick={() => setDismissed(true)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
      </Floating>
    )
  }

  if (showIos) {
    return (
      <Floating label="Instalar o FinControl">
        <div className="flex items-start justify-between gap-3">
          <p className="font-medium">Instale o FinControl no iPhone</p>
          <button type="button" aria-label="Dispensar" onClick={dismissIos} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <ol className="mt-2 space-y-1.5 text-[13px] text-muted-foreground list-decimal pl-4">
          <li>No Safari, toque em <Share className="inline h-3.5 w-3.5 mx-0.5 align-[-2px]" aria-label="Compartilhar" /> <strong className="text-foreground font-medium">Compartilhar</strong>.</li>
          <li>Escolha <strong className="text-foreground font-medium">Adicionar à Tela de Início</strong>.</li>
          <li>Toque em <strong className="text-foreground font-medium">Adicionar</strong>.</li>
        </ol>
      </Floating>
    )
  }

  if (canInstall) {
    return (
      <Floating label="Instalar o FinControl">
        <div className="flex items-center gap-3">
          <Download className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium leading-tight">Instale o FinControl</p>
            <p className="text-[12px] text-muted-foreground leading-tight">Abre direto da tela inicial, como um app.</p>
          </div>
          <Button size="sm" onClick={install}>Instalar</Button>
          <button type="button" aria-label="Dispensar" onClick={() => setDismissed(true)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
      </Floating>
    )
  }

  return null
}
